import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Badge } from "@groot/ui/badge";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Receipt } from "@/types";
import dayjs from "dayjs";
import { ArrowLeft, Download, Mail, Maximize2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function ReceiptView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (id) {
      loadReceipt(Number.parseInt(id));
    }
  }, [id]);

  const loadReceipt = async (receiptId: number) => {
    try {
      setLoading(true);
      const data = await apiClient.getReceipt(receiptId);
      setReceipt(data);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load receipt",
      });
      console.error(err);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date?: string | null) => {
    if (!date) return "—";
    return dayjs(date).format("MMMM D, YYYY");
  };

  const formatFileSize = (bytes?: number | null) => {
    if (!bytes) return "Unknown";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!receipt) {
    return null;
  }

  const extractedData = receipt.extractedData;

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title={receipt.merchantName || "Receipt Details"}
        description={formatDate(receipt.receiptDate)}
        className="mb-8"
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="size-4" />
              Back
            </Button>
            <Button asChild>
              <a
                href={apiClient.getReceiptPdfUrl(receipt.id)}
                download={receipt.fileName}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="size-4" />
                Download
              </a>
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Details Section */}
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4">
              <h2 className="text-base font-medium text-foreground">Summary</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Merchant</span>
                <span className="font-medium">{receipt.merchantName || "Unknown"}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Date</span>
                <span className="font-medium">{formatDate(receipt.receiptDate)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Amount</span>
                <Badge variant="default" className="text-base px-3 py-1">
                  {receipt.totalAmount != null && (
                    <AmountDisplay
                      amount={receipt.totalAmount}
                      currencyCode={receipt.currency || "INR"}
                    />
                  )}
                </Badge>
              </div>

              {extractedData?.receiptNumber && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Receipt #</span>
                  <span className="font-medium">{extractedData.receiptNumber}</span>
                </div>
              )}

              {extractedData?.paymentMethod && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Payment Method</span>
                  <span className="font-medium">{extractedData.paymentMethod}</span>
                </div>
              )}
            </div>
          </div>

          {/* Line Items */}
          {receipt.lineItems && receipt.lineItems.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="border-b border-border bg-muted px-6 py-4">
                <h2 className="text-base font-medium text-foreground">
                  Line Items ({receipt.lineItems.length})
                </h2>
              </div>
              <div className="divide-y divide-border">
                {receipt.lineItems.map((item, index) => {
                  const lineItemKey =
                    (item as { id?: string | number }).id ??
                    `${item.description}-${item.amount}-${item.quantity ?? ""}-${index}`;

                  return (
                    <div key={lineItemKey} className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.description}</p>
                          <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                            {item.quantity && <span>Qty: {item.quantity}</span>}
                            {item.purity && <span>Purity: {item.purity}</span>}
                            {item.netWeight && <span>Net Wt: {item.netWeight}g</span>}
                            {item.grossWeight && <span>Gross Wt: {item.grossWeight}g</span>}
                          </div>
                        </div>
                        <Badge variant="secondary">
                          <AmountDisplay
                            amount={item.amount}
                            currencyCode={receipt.currency || "INR"}
                          />
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Amount Breakdown */}
          {extractedData && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="border-b border-border bg-muted px-6 py-4">
                <h2 className="text-base font-medium text-foreground">Amount Breakdown</h2>
              </div>
              <div className="p-6 space-y-3">
                {extractedData.subtotal != null && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Subtotal</span>
                    <span>
                      <AmountDisplay
                        amount={extractedData.subtotal ?? 0}
                        currencyCode={receipt.currency || "INR"}
                      />
                    </span>
                  </div>
                )}
                {extractedData.taxTotal != null && extractedData.taxTotal > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Tax</span>
                    <span>
                      <AmountDisplay
                        amount={extractedData.taxTotal ?? 0}
                        currencyCode={receipt.currency || "INR"}
                      />
                    </span>
                  </div>
                )}
                {extractedData.discountTotal != null && extractedData.discountTotal > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Discount</span>
                    <span className="text-success">
                      −
                      <AmountDisplay
                        amount={extractedData.discountTotal}
                        currencyCode={receipt.currency || "INR"}
                      />
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-border">
                  <span className="font-medium">Total</span>
                  <span className="font-semibold text-lg">
                    <AmountDisplay
                      amount={extractedData.totalAmount ?? 0}
                      currencyCode={receipt.currency || "INR"}
                    />
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* File Details */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4">
              <h2 className="text-base font-medium text-foreground">File Details</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">File Name</p>
                <p className="font-medium">{receipt.fileName}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">File Size</p>
                <p className="font-medium">{formatFileSize(receipt.fileSize)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Uploaded At</p>
                <p className="font-medium">
                  {dayjs(receipt.createdAt).format("MMM D, YYYY h:mm A")}
                </p>
              </div>
              {receipt.notes && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{receipt.notes}</p>
                </div>
              )}
              {receipt.sourceEmail && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="size-3" />
                    Ingested from Email
                  </p>
                  <a
                    href={`/inbound-emails/${receipt.sourceEmail.id}`}
                    className="text-sm text-info hover:underline"
                  >
                    {receipt.sourceEmail.subject || "(No Subject)"}
                  </a>
                  {receipt.sourceEmail.from && (
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      From: {receipt.sourceEmail.from}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PDF Viewer Section */}
        <div>
          <div className="border border-border rounded-lg overflow-hidden sticky top-4">
            <div className="border-b border-border bg-muted px-6 py-4 flex items-center justify-between">
              <h2 className="text-base font-medium text-foreground">PDF Document</h2>
              <Button variant="outline" size="sm" onClick={() => setIsFullScreen(true)}>
                <Maximize2 className="size-4 mr-2" />
                Full Screen
              </Button>
            </div>
            <div className="p-6">
              <div className="w-full aspect-[8.5/11] bg-muted rounded-lg overflow-hidden">
                <iframe
                  src={apiClient.getReceiptPdfUrl(receipt.id)}
                  className="w-full h-full border-0"
                  title="Receipt PDF"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen PDF Dialog */}
      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>
              {receipt.merchantName || "Receipt"} - {formatDate(receipt.receiptDate)}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6 min-h-0">
            <div className="w-full h-full bg-muted rounded-lg overflow-hidden">
              <iframe
                src={apiClient.getReceiptPdfUrl(receipt.id)}
                className="w-full h-full border-0"
                title="Receipt PDF - Full Screen"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
